import {
    CADENCE_OPTIONS,
    TARGET_FORMAT_OPTIONS,
    TIMECODE_DIGIT_OPTIONS,
} from '../presets';
import { FieldConfig, FormConfig } from '@features/dynamicForm/types';
import ISO6391 from 'iso-639-1';
import { useTranslation } from 'react-i18next';

import { OrderType, WizardAccumulatedData } from '../types';
import { useMemo } from 'react';

const capitalize = (str?: string) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

export const useCoreOrderConfigs = (
    orderType?: OrderType,
): FormConfig<WizardAccumulatedData>[] => {
    const { t, i18n } = useTranslation('serviceOrders');

    return useMemo(() => {
        //~ 1. DYNAMIC DATA WITH NATIVE LOCALIZATION
        const languageNames = new Intl.DisplayNames([i18n.language || 'en'], {
            type: 'language',
        });

        const languageOptions = ISO6391.getAllCodes()
            .map((code) => {
                const localizedName = capitalize(languageNames.of(code));
                const nativeName = capitalize(ISO6391.getNativeName(code));

                const label =
                    localizedName === nativeName
                        ? localizedName
                        : `${localizedName} (${nativeName})`;

                return {
                    label,
                    value: code,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label, i18n.language));

        //~ 2. REUSABLE FIELD GROUPS
        const videoSpecsFields: FieldConfig<WizardAccumulatedData>[] = [
            {
                name: 'format',
                label: t('fields.format.label'),
                type: 'select',
                placeholder: t('fields.format.placeholder'),
                options: TARGET_FORMAT_OPTIONS,
                validation: { required: t('validations.required') },
            },
            {
                name: 'resolution',
                label: t('fields.resolution.label'),
                type: 'select',
                placeholder: t('fields.resolution.placeholder'),
                options: [
                    { label: '1920x1080 (HD)', value: '1080p' },
                    { label: '3840x2160 (4K)', value: '4k' },
                ],
            },
            {
                name: 'framerate',
                label: t('fields.framerate.label'),
                type: 'select',
                placeholder: t('fields.framerate.placeholder'),
                options: CADENCE_OPTIONS,
            },
        ];

        const audioTracksField: FieldConfig<WizardAccumulatedData> = {
            name: 'audioTracks',
            label: t('fields.audioTracks.label'),
            type: 'array',
            buttonLabel: t('fields.audioTracks.add_button'),
            fields: [
                {
                    name: 'layout',
                    label: t('fields.audioTracks.layout'),
                    type: 'select',
                    placeholder: t('fields.audioTracks.layout_placeholder'),
                    options: [
                        { label: 'Stereo', value: 'stereo' },
                        { label: '5.1', value: '5.1' },
                    ],
                },
                {
                    name: 'language',
                    label: t('fields.audioTracks.language'),
                    type: 'combobox',
                    placeholder: t('fields.audioTracks.language_placeholder'),
                    options: languageOptions,
                },
            ],
        };

        //~ 3. DECLARATIVE CONFIGURATION MAP
        const configMap: Partial<
            Record<OrderType, FormConfig<WizardAccumulatedData>[]>
        > = {
            transcode: [
                {
                    id: 'step-transcode-core',
                    title: t('transcode.title'),
                    description: t('transcode.description'),
                    fields: [
                        ...videoSpecsFields,
                        {
                            name: 'tcIn',
                            label: t('fields.tcIn.label'),
                            type: 'select',
                            placeholder: t('fields.tcIn.placeholder'),
                            options: [
                                { label: '00:00:00:00', value: '00:00:00:00' },
                                { label: '01:00:00:00', value: '01:00:00:00' },
                                { label: '10:00:00:00', value: '10:00:00:00' },
                            ],
                        },
                        {
                            name: 'r128',
                            label: t('fields.r128.label'),
                            type: 'checkbox',
                        },
                        audioTracksField,
                    ],
                },
            ],
            pad: [
                {
                    id: 'step-pad-core',
                    title: t('pad.title'),
                    description: t('pad.description'),
                    fields: [
                        {
                            name: 'channel',
                            label: t('fields.channel.label'),
                            type: 'text',
                            placeholder: t('fields.channel.placeholder'),
                            validation: { required: t('validations.required') },
                        },
                        audioTracksField,
                        {
                            name: 'audiodescription',
                            label: t('fields.audiodescription.label'),
                            type: 'checkbox',
                        },
                    ],
                },
            ],
            extracts: [
                {
                    id: 'step-extracts-core',
                    title: t('extracts.title'),
                    description: t('extracts.description'),
                    fields: [
                        ...videoSpecsFields,
                        audioTracksField,
                        {
                            name: 'extracts',
                            label: t('fields.extracts.label'),
                            type: 'array',
                            buttonLabel: t('fields.extracts.add_button'),
                            fields: [
                                {
                                    name: 'tcIn',
                                    label: t('fields.tcIn.label'),
                                    type: 'digit',
                                    options: TIMECODE_DIGIT_OPTIONS,
                                    validation: {
                                        required: t('validations.required'),
                                    },
                                },
                                {
                                    name: 'tcOut',
                                    label: t('fields.tcOut.label'),
                                    type: 'digit',
                                    options: TIMECODE_DIGIT_OPTIONS,
                                    validation: {
                                        required: t('validations.required'),
                                    },
                                },
                            ],
                        },
                        {
                            name: 'otherExtracts',
                            label: t('fields.otherExtracts.label'),
                            type: 'textarea',
                            placeholder: t('fields.otherExtracts.placeholder'),
                        },
                    ],
                },
            ],
            watermark: [
                {
                    id: 'step-watermark-core',
                    title: t('watermark.title'),
                    description: t('watermark.description'),
                    fields: [
                        {
                            name: 'faspexRecipient',
                            label: t('fields.faspexRecipient.label'),
                            type: 'emailTags',
                            placeholder: t(
                                'fields.faspexRecipient.placeholder',
                            ),
                            validation: { required: t('validations.required') },
                        },
                        {
                            name: 'watermarkText',
                            label: t('fields.watermarkText.label'),
                            type: 'text',
                            placeholder: t('fields.watermarkText.placeholder'),
                            validation: { required: t('validations.required') },
                        },
                        {
                            name: 'watermarkRecipient',
                            label: t('fields.watermarkRecipient.label'),
                            type: 'text',
                            placeholder: t(
                                'fields.watermarkRecipient.placeholder',
                            ),
                            validation: { required: t('validations.required') },
                        },
                    ],
                },
            ],
            proxy: [
                {
                    id: 'step-proxy-core',
                    title: t('proxy.title'),
                    description: t('proxy.description'),
                    fields: [
                        {
                            name: 'skipWatermark',
                            label: t('fields.skipWatermark.label'),
                            type: 'checkbox',
                        },
                    ],
                },
            ],
            erase_proxy: [],
            deletion: [],
            // erase_proxy: [
            //     {
            //         id: 'step-erase-proxy-core',
            //         title: t('erase_proxy.title'),
            //         description: t('erase_proxy.description'),
            //         fields: [],
            //     },
            // ],
            // deletion: [
            //     {
            //         id: 'step-deletion-core',
            //         title: t('deletion.title'),
            //         description: t('deletion.description'),
            //         fields: [
            //             {
            //                 name: 'deletionReason',
            //                 label: t('fields.deletionReason.label'),
            //                 type: 'textarea',
            //                 placeholder: t('fields.deletionReason.placeholder'),
            //                 validation: { required: t('validations.required') },
            //             },
            //         ],
            //     },
            // ],
            other: [
                {
                    id: 'step-other-core',
                    title: t('other.title'),
                    description: t('other.description'),
                    fields: [
                        {
                            name: 'otherRequestDescription',
                            label: t('fields.otherRequestDescription.label'),
                            type: 'textarea',
                            placeholder: t(
                                'fields.otherRequestDescription.placeholder',
                            ),
                            validation: { required: t('validations.required') },
                        },
                    ],
                },
            ],
        };

        //~ 4. SAFE LOOKUP
        return (orderType ? configMap[orderType] : []) ?? [];
    }, [i18n.language, orderType, t]);
};
